/* eslint-disable indent */
import { datadogLogs } from '@datadog/browser-logs';
import { trackAquisitionCompleted } from './segment';
import { getCookieValue, setCookieValue } from './cookie';
import checkForGlobalProperty from './checkForGlobalProperty';
import firePurchaseEvent from './firePurchaseEvent';
import {
  IResponseError,
  IProspect,
  ICountry,
  ICustomizeOptions,
  PaymentType,
  EPayContext,
  ILineItem,
} from '../../interfaces';
import { postRegister } from '../services/registerApi';
import { ACTIVE_USER_EXISTS, COOKIE, INACTIVE_USER_EXISTS } from './constants';

const TRADE_DESK_PIXEL_LOADED_PROPERTY = 'tradeDeskPixelLoaded';

const redirectToThankYouCallBack = (subscriptionUuid: string) => {
  window.location.assign(
    `/gtb-thank-you/shipping/?subhash=${subscriptionUuid}&get-the-box&step=confirm`
  );
};

export const handleRegistration = async (
  token: string,
  prospect: IProspect & { firstName: string; lastName: string },
  customize: ICustomizeOptions,
  paymentType: PaymentType,
  payContext: EPayContext,
  countries: ICountry[],
  hCaptchaToken: string,
  handleOnSubmitError: (error: IResponseError | any, autologin: boolean) => void,
  transactionTotal: number,
  handleExperimentTrackingOnSuccess: (
    shopUserId: string,
    subscriptionUuid: string,
    billingType: PaymentType,
    planCode: string,
    discount: string,
    total: number
  ) => void,
  handleBlotoutRegisterTracking: (data: any) => void,
  cartId?: number | undefined,
  sku?: string,
  lineItems?: ILineItem[],
) => {
  const fbpCookie = getCookieValue('_fbp') || '';
  const fbcCookie = getCookieValue('_fbc') || '';
  const {
    firstName,
    lastName,
    postalCode,
    shipState,
    shipCountry,
    email,
    planCode,
    discount,
    total,
  } = prospect;
  const registerCustomize =
    payContext === 'intro'
      ? {
          campaignId: 0,
          selectedProduct: null,
        }
      : customize;
  try {
    const response = await postRegister(
      token,
      prospect,
      { firstName, lastName },
      registerCustomize as ICustomizeOptions,
      paymentType,
      countries,
      fbpCookie,
      fbcCookie,
      hCaptchaToken,
      payContext,
      cartId,
    );
    const { subscriptionUuid, shopUserId } = response;
    handleBlotoutRegisterTracking({
      firstName,
      lastName,
      city: postalCode,
      state: shipState,
      country: shipCountry,
      email,
    });
    trackAquisitionCompleted(sku, 'Success', paymentType, payContext);
    firePurchaseEvent(
      shopUserId,
      subscriptionUuid,
      prospect,
      payContext,
      transactionTotal,
      lineItems,
    );
    handleExperimentTrackingOnSuccess(
      shopUserId,
      subscriptionUuid,
      paymentType,
      planCode,
      discount,
      total
    );
    setCookieValue(COOKIE.key, COOKIE.value, 1);
    checkForGlobalProperty(TRADE_DESK_PIXEL_LOADED_PROPERTY, () =>
      redirectToThankYouCallBack(subscriptionUuid)
    );
  } catch (err: any | IResponseError) {
    // we make sure to track all events even if the response is an error
    trackAquisitionCompleted(sku, err.eventMessage, paymentType, payContext);
    let isAutoLogin = false;
    let errorMessage = '';
    if (err.errorCode === 'UNKNOWN_ERROR') {
      // eslint-disable-next-line max-len
      errorMessage =
        'There was an error processing your request. Please contact customer support for further assistance.';
    } else if (
      err.errorCode === ACTIVE_USER_EXISTS ||
      err.errorCode === INACTIVE_USER_EXISTS
    ) {
      isAutoLogin = true;
      errorMessage = { ...err };
    } else {
      errorMessage = err.message;
    }
    handleOnSubmitError(errorMessage, isAutoLogin);
    datadogLogs.logger.error('On payment submit error: ', {}, err as Error);
  }
};

export default handleRegistration;
